import { useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/cn.js';

interface AccountMenuProps {
  readonly email: string;
  readonly onSignOut: () => void;
  readonly icon: React.ReactNode;
}

export function AccountMenu({ email, onSignOut, icon }: AccountMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const initial = email.charAt(0).toUpperCase();

  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'grid h-8 w-8 cursor-pointer place-items-center rounded-full border text-xs font-semibold uppercase transition-colors',
          open
            ? 'border-accent/50 bg-accent/15 text-text-primary'
            : 'border-border-subtle bg-surface text-text-secondary hover:border-accent/40 hover:bg-hover',
        )}
        aria-label="Account menu"
      >
        {initial}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-64 animate-slide-up overflow-hidden rounded-xl border border-border-subtle bg-surface shadow-panel">
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Signed in as</div>
            <div className="mt-1 truncate font-mono text-sm text-text-primary">{email}</div>
          </div>
          <div className="border-t border-border-subtle">
            <button
              type="button"
              onClick={onSignOut}
              className="flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-left text-sm text-text-secondary transition-colors hover:bg-hover hover:text-text-primary"
            >
              {icon}
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
